/**
 * Utility functions for handling API errors and validation messages
 */

export interface ValidationError {
    [field: string]: string | string[];
}

export interface ApiError {
    message: string;
    status?: number;
    data?: any;
    response?: any;
}

/**
 * Format validation errors into a readable message
 */
export function formatValidationErrors(errors: ValidationError): string {
    if (!errors || typeof errors !== 'object') {
        return 'Validation failed';
    }

    const errorMessages: string[] = [];

    for (const [field, messages] of Object.entries(errors)) {
        // Convert field name to readable format (e.g., "lot_size" -> "Lot Size")
        const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, function (letter) {
            return letter.toUpperCase();
        });

        // Handle both string and array of strings
        const messageArray = Array.isArray(messages) ? messages : [messages];
        errorMessages.push(`${fieldName}: ${messageArray.join(', ')}`);
    }

    return errorMessages.join('\n') || 'Validation failed';
}

/**
 * Extract error message from various error object structures
 */
export function extractErrorMessage(error: any): string {
    if (!error) return 'An unknown error occurred';

    // Check various possible locations for error messages
    if (error.data?.message) return error.data.message;
    if (error.data?.error) return error.data.error;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.data?.error) return error.response.data.error;
    if (error.message) return error.message;
    if (typeof error === 'string') return error;

    return 'An unknown error occurred';
}

/**
 * Check if an error is a validation error (422 status)
 */
export function isValidationError(error: any): boolean {
    return error?.status === 422 || error?.response?.status === 422;
}

/**
 * Get validation errors from error object
 */
export function getValidationErrors(error: any): ValidationError | null {
    if (!isValidationError(error)) return null;

    return error.response?.data?.errors || error.data?.errors || null;
}

/**
 * Get the first validation error message for display (e.g. in a toast).
 * Prefer showing a specific validation message over a generic "Validation failed".
 */
export function getFirstValidationMessage(error: any): string | null {
    const errors = getValidationErrors(error);
    if (!errors || typeof errors !== 'object') return null;
    const firstKey = Object.keys(errors)[0];
    if (!firstKey) return null;
    const messages = errors[firstKey];
    const msg = Array.isArray(messages) ? messages[0] : messages;
    return typeof msg === 'string' ? msg : null;
}

/**
 * Get the best user-facing message for an error: validation message first, then API message, then fallback.
 */
export function getErrorMessage(error: any, fallback: string): string {
    return getFirstValidationMessage(error) || extractErrorMessage(error) || fallback;
}

/**
 * Format error message for display with enhanced context
 */
export function formatErrorForDisplay(error: any): { message: string; isValidationError: boolean } {
    const isValidationErrorFlag = isValidationError(error);
    let message = extractErrorMessage(error);

    if (isValidationErrorFlag) {
        const validationErrors = getValidationErrors(error);
        if (validationErrors) {
            message = `Validation Error:\n${formatValidationErrors(validationErrors)}`;
        }
    } else {
        // Enhanced error message handling for specific error types
        const lowerMessage = message.toLowerCase();

        // Handle new detailed error messages from backend
        if (lowerMessage.includes('trading authorization failed')) {
            // Extract specific details from the error message
            if (lowerMessage.includes('kyc verification is required')) {
                message =
                    'KYC verification is required. Please complete your identity verification before trading.';
            } else if (lowerMessage.includes('account is not active')) {
                message = 'Your account is not active. Please contact support for assistance.';
            } else {
                message = 'Trading authorization failed. Please check your account status.';
            }
        } else if (lowerMessage.includes('leverage validation failed')) {
            // The backend now provides detailed leverage error messages
            message = message; // Use the detailed message as-is
        } else if (lowerMessage.includes('lot size validation failed')) {
            // The backend now provides detailed lot size error messages
            message = message; // Use the detailed message as-is
        } else if (lowerMessage.includes('insufficient') || lowerMessage.includes('margin')) {
            message = 'Insufficient margin for this order. Please check your account balance.';
        } else if (lowerMessage.includes('maximum leverage')) {
            message = `Leverage exceeds maximum allowed for this instrument. ${message}`;
        } else if (lowerMessage.includes('minimum lot') || lowerMessage.includes('lot size')) {
            message = 'Lot size is below the minimum allowed for this instrument.';
        } else if (lowerMessage.includes('maximum lot') || lowerMessage.includes('lot size')) {
            message = 'Lot size exceeds the maximum allowed for this instrument.';
        } else if (
            lowerMessage.includes('market closed') ||
            lowerMessage.includes('trading hours')
        ) {
            message = 'Market is currently closed. Please try again during trading hours.';
        } else if (lowerMessage.includes('invalid price') || lowerMessage.includes('price')) {
            message = 'Invalid price specified. Please check your order parameters.';
        } else if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
            message = 'Network error. Please check your connection and try again.';
        } else if (error.status === 400 || error.response?.status === 400) {
            message = `Invalid order request: ${message}`;
        } else if (error.status === 401 || error.response?.status === 401) {
            message = 'Authentication required. Please log in again.';
        } else if (error.status === 403 || error.response?.status === 403) {
            // Enhanced 403 error handling with specific permission messages
            message = formatPermissionError(error);
        } else if (error.status === 500 || error.response?.status === 500) {
            message = 'Server error. Please try again later.';
        }
    }

    return { message, isValidationError: isValidationErrorFlag };
}

/**
 * Format permission-related error messages for better user experience
 */
export function formatPermissionError(error: any): string {
    const errorMessage = extractErrorMessage(error);
    const lowerMessage = errorMessage.toLowerCase();

    // Map specific backend error messages to user-friendly messages
    const permissionMappings: Record<string, string> = {
        // General permission messages
        unauthorized: 'You do not have permission to perform this action.',
        'insufficient permissions': 'You do not have sufficient permissions for this action.',
        'access denied': 'Access denied. You may not have permission for this action.',

        // Module-specific permission messages
        customers: 'You do not have permission to access customer data.',
        leads: 'You do not have permission to access lead data.',
        users: 'You do not have permission to access user data.',
        kyc: 'You do not have permission to access KYC data.',
        trades: 'You do not have permission to access trading data.',
        transactions: 'You do not have permission to access transaction data.',
        trading_accounts: 'You do not have permission to access trading account data.',
        notes: 'You do not have permission to access notes.',
        audit_log: 'You do not have permission to access audit logs.',
        system_settings: 'You do not have permission to access system settings.',
        roles: 'You do not have permission to access role management.',
        instruments: 'You do not have permission to access instrument data.',
        assets: 'You do not have permission to access asset data.',

        // Action-specific permission messages
        view: 'You do not have permission to view this data.',
        edit: 'You do not have permission to edit this data.',
        create: 'You do not have permission to create new records.',
        delete: 'You do not have permission to delete records.',
        export: 'You do not have permission to export data.',
        import: 'You do not have permission to import data.',

        // Scope-specific permission messages
        all: 'You do not have permission to access all records.',
        my_team: 'You do not have permission to access team data.',
        mine: 'You can only access your own data.',

        // Specific business logic messages
        'can only assign leads to yourself': 'You can only assign leads to yourself.',
        'can only assign to team members': 'You can only assign to team members.',
        'can only edit your own notes': 'You can only edit your own notes.',
        'can only delete your own notes': 'You can only delete your own notes.',
        'can only edit team notes': 'You can only edit team notes.',
        'can only delete team notes': 'You can only delete team notes.',
        'can only view your own documents': 'You can only view your own documents.',
        'can only upload for your own customers':
            'You can only upload documents for your own customers.',
        'can only view your own customers': 'You can only view your own customers.',
        'can only edit your own customers': 'You can only edit your own customers.',
        'can only delete your own customers': 'You can only delete your own customers.',
        'can only view your own leads': 'You can only view your own leads.',
        'can only edit your own leads': 'You can only edit your own leads.',
        'can only delete your own leads': 'You can only delete your own leads.',
        'can only convert your own leads': 'You can only convert your own leads.',
        'can only view your own trading accounts': 'You can only view your own trading accounts.',
        'can only edit your own trading accounts': 'You can only edit your own trading accounts.',
        'can only delete your own trading accounts':
            'You can only delete your own trading accounts.',
        'can only view your own transactions': 'You can only view your own transactions.',
        'can only edit your own transactions': 'You can only edit your own transactions.',
        'can only delete your own transactions': 'You can only delete your own transactions.',
        'can only view your own trades': 'You can only view your own trades.',
        'can only edit your own trades': 'You can only edit your own trades.',
        'can only delete your own trades': 'You can only delete your own trades.',
        'can only view your own kyc requests': 'You can only view your own KYC requests.',
        'can only edit your own kyc requests': 'You can only edit your own KYC requests.',
        'can only delete your own kyc requests': 'You can only delete your own KYC requests.',
        'can only view your own users': 'You can only view your own user data.',
        'can only edit your own users': 'You can only edit your own user data.',
        'can only delete your own users': 'You can only delete your own user data.',
        'can only view your own instruments': 'You can only view your own instruments.',
        'can only edit your own instruments': 'You can only edit your own instruments.',
        'can only delete your own instruments': 'You can only delete your own instruments.',
        'can only view your own assets': 'You can only view your own assets.',
        'can only edit your own assets': 'You can only edit your own assets.',
        'can only delete your own assets': 'You can only delete your own assets.',
        'can only view your own settings': 'You can only view your own settings.',
        'can only edit your own settings': 'You can only edit your own settings.',
        'can only delete your own settings': 'You can only delete your own settings.',
        'can only view your own roles': 'You can only view your own roles.',
        'can only edit your own roles': 'You can only edit your own roles.',
        'can only delete your own roles': 'You can only delete your own roles.',
        'can only view your own audit logs': 'You can only view your own audit logs.',
        'can only export your own data': 'You can only export your own data.',
        'can only import your own data': 'You can only import your own data.',
    };

    // Check for exact matches first
    for (const [key, value] of Object.entries(permissionMappings)) {
        if (lowerMessage.includes(key.toLowerCase())) {
            return value;
        }
    }

    // Check for partial matches and provide context
    if (lowerMessage.includes('permission') || lowerMessage.includes('unauthorized')) {
        // Try to extract module and action from the error message
        const moduleMatch = lowerMessage.match(
            /(customers|leads|users|kyc|trades|transactions|trading_accounts|notes|audit_log|system_settings|roles|instruments|assets)/
        );
        const actionMatch = lowerMessage.match(/(view|edit|create|delete|export|import)/);

        if (moduleMatch && actionMatch) {
            const module = moduleMatch[1];
            const action = actionMatch[1];
            return `You do not have permission to ${action} ${module} data.`;
        } else if (moduleMatch) {
            const module = moduleMatch[1];
            return `You do not have permission to access ${module} data.`;
        } else if (actionMatch) {
            const action = actionMatch[1];
            return `You do not have permission to ${action} this data.`;
        }
    }

    // Default fallback message
    return 'You do not have permission to perform this action. Please contact your administrator if you believe this is an error.';
}
